import { ShieldAlert, Users, Zap, type LucideIcon } from "lucide-react";
import type {
  ExposureRiskCandidate,
  ExposureRiskCategory,
  ExposureRiskCriticality,
  ExposureRiskType,
} from "@playbookai/widget-schemas";

export const EXPOSURE_RISK_TYPE_LABELS: Record<ExposureRiskType, string> = {
  exposure: "Exposure",
  containment_failure: "Containment failure",
  side_effect: "Side effect",
};

export const EXPOSURE_RISK_CATEGORY_LABELS: Record<
  ExposureRiskCategory,
  string
> = {
  safety: "Safety",
  customer: "Customer",
  quality: "Quality",
  regulatory: "Regulatory",
  delivery: "Delivery",
  operation: "Operation",
};

export const EXPOSURE_RISK_CRITICALITY_LABELS: Record<
  ExposureRiskCriticality,
  string
> = {
  unknown: "Unknown",
  low: "Low",
  medium: "Medium",
  high: "High",
  critical: "Critical",
};

export const EXPOSURE_RISK_CRITICALITY_TONE: Record<
  ExposureRiskCriticality,
  string
> = {
  unknown: "border-pb-border text-pb-text-muted bg-pb-card-elevated",
  low: "border-pb-green/35 text-pb-green-text bg-pb-green/10",
  medium: "border-pb-amber/35 text-pb-amber-text bg-pb-amber/10",
  high: "border-pb-red/35 text-pb-red-text bg-pb-red/10",
  critical: "border-pb-red/50 text-pb-red-text bg-pb-red/15",
};

export const EXPOSURE_RISK_TYPE_ICON: Record<ExposureRiskType, LucideIcon> = {
  exposure: Users,
  containment_failure: ShieldAlert,
  side_effect: Zap,
};

/** Marker colour per type — decorative grouping, not a state report. */
export const EXPOSURE_RISK_TYPE_TONE: Record<ExposureRiskType, string> = {
  exposure: "text-pb-tint-1",
  containment_failure: "text-pb-tint-2",
  side_effect: "text-pb-tint-3",
};

export const EXPOSURE_RISK_CRITICALITY_DOT: Record<
  ExposureRiskCriticality,
  string
> = {
  unknown: "bg-pb-text-muted/50",
  low: "bg-pb-green",
  medium: "bg-pb-text-muted",
  high: "bg-pb-amber",
  critical: "bg-pb-red",
};

const CRITICALITY_RANK: Record<ExposureRiskCriticality, number> = {
  unknown: 0,
  low: 1,
  medium: 2,
  high: 3,
  critical: 4,
};

export interface ExposureRiskRollup {
  readonly candidates: number;
  readonly highOrCritical: number;
  readonly categories: number;
  readonly unknown: number;
  readonly byType: Record<ExposureRiskType, number>;
}

export function exposureRiskRollup(
  risks: readonly ExposureRiskCandidate[],
): ExposureRiskRollup {
  const byType: Record<ExposureRiskType, number> = {
    exposure: 0,
    containment_failure: 0,
    side_effect: 0,
  };
  for (const risk of risks) byType[risk.type] += 1;
  return {
    candidates: risks.length,
    highOrCritical: risks.filter(
      (risk) => risk.criticality === "high" || risk.criticality === "critical",
    ).length,
    categories: new Set(risks.map((risk) => risk.category)).size,
    unknown: risks.filter((risk) => risk.criticality === "unknown").length,
    byType,
  };
}

export function sortExposureRisks(
  risks: readonly ExposureRiskCandidate[],
): ExposureRiskCandidate[] {
  return [...risks].sort(
    (a, b) =>
      CRITICALITY_RANK[b.criticality] - CRITICALITY_RANK[a.criticality] ||
      a.title.localeCompare(b.title),
  );
}
